"use client"

import { useEffect, useRef } from "react"

// ── Mapa de sonidos por tipo ──────────────────────────────────
const SOUND_MAP = {
  urgent: "/messages-sound.mp3",
  message: "/messages-sound.mp3",
  success: "/success_bell.mp3",
  default: "/messages-sound.mp3", // notification-default.mp3 no existe; usamos el mismo
}

// ── Desbloqueo de audio del navegador ─────────────────────────
// Los navegadores modernos bloquean audio.play() hasta que el
// usuario interactúe con la página. Registramos listeners
// globales que desbloquean el AudioContext en el primer gesto.
let _audioUnlocked = false

function _unlockAudio() {
  if (_audioUnlocked) return
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (Ctx) {
      const ctx = new Ctx()
      const buf = ctx.createBuffer(1, 1, 22050)
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.connect(ctx.destination)
      src.start(0)
      // También desbloquear el camino de HTMLAudioElement
      const silent = new Audio()
      silent.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
      silent.play().catch(() => { })
    }
    _audioUnlocked = true
  } catch {
    // ignorar errores
  }
}

if (typeof window !== "undefined") {
  const _events = ["click", "touchstart", "keydown"]
  const _handler = () => {
    _unlockAudio()
    _events.forEach((e) => document.removeEventListener(e, _handler, true))
  }
  _events.forEach((e) => document.addEventListener(e, _handler, { capture: true, passive: true }))
}

// ── Caché de objetos Audio precargados ────────────────────────
const _cache = {}

function _getAudio(type) {
  const src = SOUND_MAP[type] || SOUND_MAP.default
  if (!_cache[src]) {
    const a = new Audio(src)
    a.preload = "auto"
    a.volume = 1
    a.load()
    _cache[src] = a
  }
  return _cache[src]
}

// Pre-cargar todos los sonidos al importar el módulo
if (typeof window !== "undefined") {
  Object.values(SOUND_MAP).forEach((src) => {
    if (!_cache[src]) {
      const a = new Audio(src)
      a.preload = "auto"
      a.load()
      _cache[src] = a
    }
  })
}

// ── Componente ────────────────────────────────────────────────
export function NotificationSound({ play, type = "default" }) {
  const hasPlayedRef = useRef(false)

  // Resetear bandera si play vuelve a false
  useEffect(() => {
    if (!play) {
      hasPlayedRef.current = false
    }
  }, [play])

  // Reproducir sonido una vez por cada ciclo mount / play=true
  useEffect(() => {
    if (!play || hasPlayedRef.current) return
    hasPlayedRef.current = true

    const audio = _getAudio(type)

    const doPlay = () => {
      // Clonar el nodo para permitir solapamiento rápido
      const clone = audio.cloneNode()
      clone.volume = 1
      const p = clone.play()
      if (p) {
        p.catch((err) => {
          if (err.name !== "AbortError") {
            console.warn("[notification-sound] bloqueado:", err.message)
          }
        })
      }
    }

    if (audio.readyState >= 2) {
      doPlay()
    } else {
      // Si todavía no cargó, esperar y luego reproducir
      audio.addEventListener("canplaythrough", doPlay, { once: true })
    }
  }, [play, type])

  return null // Sin renderizado visual
}

export default NotificationSound
